import React from 'react'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const getVisiblePages = () => {
    const maxVisible = isMobile ? 5 : 10
    const pages = []

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let endPage = Math.min(totalPages, startPage + maxVisible - 1)

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }

  const canGoToPrevious = currentPage > 1
  const canGoToNext = currentPage < totalPages
  const visiblePages = getVisiblePages()

  return (
    <div className="flex items-center gap-2 justify-center mt-6">
      {/* Previous arrows */}
      <button
        onClick={() => onPageChange(1)}
        disabled={!canGoToPrevious}
        className={classNames(
          'w-8 h-8 border rounded flex items-center justify-center',
          {
            'border-[#DFE3E8] bg-[#EEF1F7] cursor-not-allowed':
              !canGoToPrevious,
            'border-[#DFE3E8] hover:bg-gray-50': canGoToPrevious,
          }
        )}
      >
        <span className="text-[#C4CDD5]">««</span>
      </button>

      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={!canGoToPrevious}
        className={classNames(
          'w-8 h-8 border rounded flex items-center justify-center',
          {
            'border-[#DFE3E8] bg-[#EEF1F7] cursor-not-allowed':
              !canGoToPrevious,
            'border-[#DFE3E8] hover:bg-gray-50': canGoToPrevious,
          }
        )}
      >
        <span className="text-[#C4CDD5]">‹</span>
      </button>

      {/* Page numbers */}
      {visiblePages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={classNames(
            'w-8 h-8 border rounded flex items-center justify-center text-sm font-bold',
            {
              'border-[#00B7B9] bg-white text-[#00B7B9]': currentPage === page,
              'border-[#DFE3E8] bg-white text-[#2E343D] hover:bg-gray-50':
                currentPage !== page,
            }
          )}
        >
          {page}
        </button>
      ))}

      {/* Next arrows */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!canGoToNext}
        className={classNames(
          'w-8 h-8 border rounded flex items-center justify-center',
          {
            'border-[#DFE3E8] bg-[#EEF1F7] cursor-not-allowed': !canGoToNext,
            'border-[#DFE3E8] hover:bg-gray-50': canGoToNext,
          }
        )}
      >
        <span className="text-[#C4CDD5]">›</span>
      </button>

      <button
        onClick={() => onPageChange(totalPages)}
        disabled={!canGoToNext}
        className={classNames(
          'w-8 h-8 border rounded flex items-center justify-center',
          {
            'border-[#DFE3E8] bg-[#EEF1F7] cursor-not-allowed': !canGoToNext,
            'border-[#DFE3E8] hover:bg-gray-50': canGoToNext,
          }
        )}
      >
        <span className="text-[#C4CDD5]">»»</span>
      </button>
    </div>
  )
}
